// ─── Angular Imports ─────────────────────────────────────────────────────────
import { Component, isDevMode, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// ─── Constants ───────────────────────────────────────────────────────────────
export const RETURN_URL_KEY = 'loginReturnUrl';

// ─── Third-Party Imports ─────────────────────────────────────────────────────
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

@Component({
    selector: 'app-login',
    templateUrl: 'login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class LoginComponent {
  private hasNavigated = false;

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private router: Router,
    private zone: NgZone,
    private route: ActivatedRoute,
  ) {
    this.saveReturnUrl();
    this.onListenLoginEvent();
    this.checkCurrentAuthenticated();
  }

  // ─── Return URL Handling ─────────────────────────────────────────────────────
  private saveReturnUrl(): void {
    this.route.queryParams.subscribe((params) => {
      if (isDevMode()) {
        console.log('[DEV] [LoginComponent] query params received', params);
      }
      const returnUrl: string | undefined = params['returnUrl'];
      if (returnUrl) {
        if (isDevMode()) {
          console.log('[DEV] [LoginComponent] saving returnUrl to sessionStorage', returnUrl);
        }
        sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
      } else {
        if (isDevMode()) {
          console.log('[DEV] [LoginComponent] no returnUrl in query params');
        }
      }
    });
  }

  private getAndClearReturnUrl(): string {
    const storedUrl = sessionStorage.getItem(RETURN_URL_KEY);
    if (isDevMode()) {
      console.log('[DEV] [LoginComponent] retrieved from sessionStorage', storedUrl);
    }
    const returnUrl = storedUrl ?? '/dashboard';
    sessionStorage.removeItem(RETURN_URL_KEY);
    if (isDevMode()) {
      console.log('[DEV] [LoginComponent] will navigate to', returnUrl);
    }
    return returnUrl;
  }

  private isOAuthInProgress(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code');
  }

  // ─── Event Listeners ─────────────────────────────────────────────────────────
  private onListenLoginEvent() {
    // Listen for authentication events
    Hub.listen('auth', ({ payload: { event } }) => {
      this.handleLoginEvent(event);
    });
  }

  private handleLoginEvent(event: string): void {
    if (isDevMode()) {
      console.log('[DEV] [LoginComponent] auth event received', event);
    }
    if (this.hasNavigated) {
      if (isDevMode()) {
        console.log('[DEV] [LoginComponent] already navigated, skipping');
      }
      return;
    }
    if (event === 'signInWithRedirect' || event === 'signedIn') {
      this.hasNavigated = true;
      const returnUrl = this.getAndClearReturnUrl();
      if (isDevMode()) {
        console.log('[DEV] [LoginComponent] navigating after login event to', returnUrl);
      }
      this.zone.run(() => this.router.navigate([returnUrl]));
    }
  }

  // ─── Authentication ──────────────────────────────────────────────────────────
  private checkCurrentAuthenticated(): void {
    // OAuth in progress - Hub event will handle navigation
    if (this.isOAuthInProgress()) {
      if (isDevMode()) {
        console.log('[DEV] [LoginComponent] OAuth in progress, Hub will handle navigation');
      }
      return;
    }

    if (isDevMode()) {
      console.log('[DEV] [LoginComponent] checking if user is already authenticated');
    }
    getCurrentUser()
      .then(() => {
        if (isDevMode()) {
          console.log('[DEV] [LoginComponent] user is already authenticated');
        }
        if (this.hasNavigated) {
          if (isDevMode()) {
            console.log('[DEV] [LoginComponent] already navigated, skipping');
          }
          return;
        }
        this.hasNavigated = true;
        const returnUrl = this.getAndClearReturnUrl();
        if (isDevMode()) {
          console.log('[DEV] [LoginComponent] navigating (already auth) to', returnUrl);
        }
        this.router.navigate([returnUrl], { replaceUrl: true });
      })
      .catch((err) => {
        if (isDevMode()) {
          console.log('[DEV] [LoginComponent] user is not authenticated', err);
        }
      });
  }

  onLoginClick() {
    if (isDevMode()) {
      console.log('[DEV] [LoginComponent] login button clicked');
    }
    signInWithRedirect();
  }
}
